import type { components } from '@pba/types-openapi-client';
import { addMinutes } from 'date-fns';
import type {
  ContentObjectPollChoiceDataObject,
  ContentObjectPollQuestionDataObject,
  UploadedFileDataObject,
} from './service';
import { type UploadedFile, createUploadedFile } from './uploaded-file';

export type Poll = {
  attachments?: UploadedFile[];
  description: string;
  endTime: Date;
  htmlDescription: string;
  id?: string;
  pinned: boolean;
  postInCommunities: PollPostInCommunity[];
  programsTopicId?: string;
  questions: PollQuestion[];
  startTime: Date;
  title: string;
};

export type PollChoice = {
  id: string;
  name: string;
  voted: boolean;
  votesCount: number;
};

export type PollDataObject = {
  attachments?: UploadedFileDataObject[];
  description?: string;
  end_time?: string;
  html_description?: string;
  id?: string;
  pinned?: boolean;
  post_in_communities?: Array<components['schemas']['CommunityListGlobal']>;
  programs_topic?: { id: string };
  questions?: ContentObjectPollQuestionDataObject[];
  start_time?: string;
  title?: string;
};

export type PollPostInCommunity = {
  id: string;
  name: string;
};

export type PollQuestion = {
  choices: PollChoice[];
  id: string;
  multiple: boolean;
  question: string;
  questionVotes: number;
};

const createPollChoice = (data: ContentObjectPollChoiceDataObject): PollChoice => ({
  id: data.id,
  name: data.name,
  voted: data.voted,
  votesCount: data.votes_count,
});

const createPollQuestion = (data: ContentObjectPollQuestionDataObject): PollQuestion => ({
  choices: data.choices.map((choice) => createPollChoice(choice)),
  id: data.id,
  multiple: data.multiple,
  question: data.question,
  questionVotes: data.question_votes,
});

export const createPoll = (data: PollDataObject): Poll => {
  const startTime = data.start_time ? new Date(data.start_time) : addMinutes(new Date(), 10);
  const endTime = data.end_time ? new Date(data.end_time) : addMinutes(startTime, 60);

  return {
    attachments: data.attachments?.map((attachment) => createUploadedFile(attachment)),
    description: data.description ?? '',
    endTime,
    htmlDescription: data.html_description ?? '',
    id: data.id,
    pinned: data.pinned ?? false,
    postInCommunities: data.post_in_communities?.map((community) => ({ id: community.id, name: community.name })) ?? [],
    programsTopicId: data.programs_topic?.id,
    questions: data.questions?.map((question) => createPollQuestion(question)) ?? [],
    startTime,
    title: data.title ?? '',
  };
};
